/**
 * This is just a wrapper over the previous ApexChat.QuestionTypeRenderer
 * with extended functionality of handling FullText and QuickReply a sper CHAT-6960 feature-set
 *
 * */
import Utils from '../core/Utils';
import BaseClass from '../core/BaseClass';
import WidgetTypes from '../core/enums/WidgetTypes';
import TempCache from '../core/enums/TempCache';
import AutoComplete from '../core/AutoComplete';
import PhoneValidationHandler from '../core/validators/PhoneValidationHandler';
import PhoneCountryData from '../core/validators/PhoneContryData';
import ChatFlowEnableFlag from '../core/hooks/ChatFlowEnableFlag';
import ManageFocusOnKeyEvents from '../core/hooks/ManageFocusOnKeyEvents';
import PipedMessageHandler from '../core/hooks/PipedMessageHandler';
import { ScriptsQuestionType, SystemMessageType, VisitorResponseType } from '../core/enums/Enum';
import checkSpanishLanguage from '../core/hooks/checkSpanishLanguage';
import toTitleCase from '../core/hooks/toTitleCase';
import isLastMessage from '../core/hooks/isLastMessage';
import isQTMessageMapped from '../core/hooks/isQTMessageMapped';
import setPlaceholder from '../core/hooks/setPlaceholder';
import TempStorageCache from '../core/hooks/TempStorageCache';
import Browser from '../core/hooks/Browser';
import FullTextValidationMessages from '../core/hooks/FullTextValidationMessages';
import validateEmailWithEmoji from '../core/hooks/validateEmailWithEmoji';
import ScriptsQuestionTypeToolTips from '../core/hooks/ScriptsQuestionTypeToolTips';
import isMobile from '../isMobile';
import validateZipCode from '../core/hooks/validateZipCode';
import validateName from '../core/hooks/validateName';
import isAcceptKey from '../core/hooks/isAcceptKey';
import hideElement from '../core/hooks/hideElement';
import disableTypingInput from '../core/hooks/disableTypingInput';
import enableTypingInput from '../core/hooks/enableTypingInput';
import showValidationTooltip from '../core/hooks/showValidationTooltip';
import removeValidationTooltip from '../core/hooks/removeValidationTooltip';
import logIt from '../../logIt';

export default class QuestionTypeRendererExtentions extends BaseClass {
    constructor() {
        super({});
        logIt('QTEXT:: QuestionTypeRendererExtentions init');

        // do whatever pre-init work u wanna do here
    }

    async initComponent(options) {
        // we call this method to explicitly initialize things
        const me = this;
        if (typeof ApexChat.ApexChatJquery === 'function') {
            me.$ = ApexChat.ApexChatJquery;
        }
        me.options = options;
        if (options.widgetType === WidgetTypes.PreChat) {
            me.inputTitle = FullTextValidationMessages.FTMessage;
        } else {
            me.inputTitle = me.getQTPlaceHolder();
        }
        me.initiator = me.options.widgetType;
        me.enableFullTextLogic = options.enableFullTextLogic;
        me.lastMessage = null;
        me.isLastFullTextValidated = false; // this will be used to track last fulltext message response
        me.inputContainer = me.options.inputContainer;
        me.isChatStarted = true;
        const iframeElm = document.querySelector(`.${me.initiator}_frame`);
        me.context =
            (iframeElm && (iframeElm.contentWindow || iframeElm.contentDocument).document) ||
            me.options.context;
        const module = await import(/* webpackChunkName: "questionTypeRenderer" */ './QuestionTypeRenderer')
        const QuestionTypeRenderer = module.default;
        me.questionTypeRenderer = new QuestionTypeRenderer(me.options);
        logIt(`QTEXT:: chat initiator info:${me.initiator}`);

        // phone validator work start
        me.phoneValidator = Object.create(PhoneValidationHandler);
        if (me.inputContainer) {
            me.textAreaSelector = me.inputContainer.querySelector('textarea');
            me.autoCompleteInput = me.inputContainer.querySelector('input');
        }

        me.getCountryCode = function (options) {
            if (options.countryCode) {
                return options.countryCode;
            }
            if (options.model && options.model.countryCode) {
                return options.model.countryCode;
            }
            return 'OTHER';
        };
        const countryCode = me.getCountryCode(me.options);
        me.phoneValidator.inputField = me.autoCompleteInput;
        // me.phoneValidator.inputField = me.textAreaSelector;
        me.phoneValidator.phoneCountryData = PhoneCountryData[countryCode];
        me.addPhoneNumberPrefixCallback = me.phoneValidator.addPhonePrefixNumber.bind2(
            me.phoneValidator
        );
        // phone validator work end
    }

    validateFullTextMessage(res, options) {
        // CHAT-7003, CHAT-7004, CHAT-7005: validate FullText
        const me = this;
        me.isFromPreChat = options && options.isFromPreChat;
        me.isPredefinedResponse = options && options.isPredefinedResponse;
        let result = true; // for any other messages
        const $txt = typeof me.inputContainer === 'string' ?
            me.context.querySelector(me.inputContainer)?.getElementsByTagName('textarea')[0]
            : me.inputContainer?.getElementsByTagName('textarea')[0];
        let titleCaseName = '';
        if (!me.enableFullTextLogic) {
            return true;
        }
        logIt("QTEXT:: Validating visitor's response...");
        if (me.messageAskingForContactInfo(me.lastMessage)) {
            logIt(`QTEXT::last message's response type expected:${me.lastMessage.type}`);
            if (me.lastMessage.visitorResponseType == VisitorResponseType.Name) {
                result = me.validateName(res);
                if (!result) {
                    if ($txt) {
                        $txt?.value = '';
                    }
                    me.showError(FullTextValidationMessages.FTName);
                } else {
                    titleCaseName = toTitleCase(res); // FT Name in title case
                    ApexChat.localStorageAPI.Store(TempCache.VISITOR_NAME, res)
                }
            } else if (me.lastMessage.visitorResponseType == VisitorResponseType.Phone) {
                result = me.phoneValidator.validate(res);
                if (!result) {
                    me.showError(me.phoneValidator.getInvalidMessageText());
                }
                else {
                    me.savePhoneNumber(res);
                }
            } else if (me.lastMessage.visitorResponseType == VisitorResponseType.Email) {
                result = me.validateEmail(res);
                if (!result) {
                    if ($txt) {
                        $txt.value = '';
                    }
                    me.showError(FullTextValidationMessages.FTEmail);
                }
            } else if (me.lastMessage.visitorResponseType == VisitorResponseType.Region) {
                result = me.validateZipCode(res);
                if (!result && $txt) {
                    // CHAT-8754 Invalid answer stays on input field for Full Text Region.
                    $txt.value = '';
                    me.showError(FullTextValidationMessages.FTZipcode);
                }
            } else if (me.lastMessage.visitorResponseType == VisitorResponseType.Message) {
                result = res && res.length > 0;
                if (!result) {
                    me.showError(FullTextValidationMessages.FTMessage);
                }
            }
            logIt(`QTEXT:: response validated to be:${result}`);
            if (result) {
                // valid response? call callback
                if (
                    me.options.elements &&
                    me.$(me.options.elements.textboxContainerSelector, me.context)
                ) {
                    me.$(me.options.elements.textboxContainerSelector, me.context).removeClass(
                        'livechat_tooltip_Error_Enabled'
                    );
                }
                if (me.inputContainer) {
                    removeValidationTooltip($txt, {
                        toolTipCss: 'livechat_validation_tooltip'
                    });
                }
                TempStorageCache.del(TempCache.INPUT_OLD_VALUE); // clear cache as FT has passed validation
                hideElement(me.context.querySelector('.apexchat_skip_input')) // hide FT skip input button
                me.isLastFullTextValidated = true;
                const updatedRes = titleCaseName == '' ? res : titleCaseName;
                const answerArray = [];
                const response = {
                    chatId: me.lastMessage.chatId,
                    isSkipped: false,
                    isPredefinedResponse: me.isPredefinedResponse,
                    msgText: res,
                    agentScriptAnswerId: me.lastMessage.scriptAnswerId,
                    chatLogId: me.lastMessage.id,
                    questionType: ScriptsQuestionType.FullText
                };
                response.questionTypeChatLogIdToBeUpdated = me.lastMessage.id;
                answerArray.push({ option: updatedRes });
                response.answers = JSON.stringify(answerArray);
                logIt('FT::disabling input until FT response  is processed:');
                logIt(response);
                me.disableTypingInput();
                if (
                    me.questionTypeRenderer.options &&
                    me.questionTypeRenderer.options.responseCallback
                ) {
                    me.questionTypeRenderer.options.responseCallback({
                        res: response,
                        cb: () => {
                            if ($txt) {
                                $txt.value = ''; // we need to explicitely clear input after a valid FT response
                            }
                        }
                    });
                }
            } else {
                // me.setLastFullText();//on invalid FT input, don't lose it so visitor has a chance to correct it
            }
        }
        return result;
    }

    skipFullText() {
        const me = this;
        me.$('.apexchat_skip_input', me.context).hide(); // hide FT skip input button
        const response = {
            chatId: me.lastMessage.chatId,
            isSkipped: true,
            answers: '[{ "option": "' + '' + '"}]',
            msgText: '',
            agentScriptAnswerId: me.lastMessage.scriptAnswerId,
            chatLogId: me.lastMessage.id,
            questionType: ScriptsQuestionType.FullText
        };
        if (me.questionTypeRenderer.options && me.questionTypeRenderer.options.responseCallback) {
            me.questionTypeRenderer.options.responseCallback({
                res: response,
                cb() {
                    logIt('FT:: now input should be enabled');
                }
            });
        }
    }

    startChat(settings) {
        const me = this;
        logIt('QTEXT:: Staring Chat...');
        if (!settings) {
            return; // no settings? no chat
        }
        // set or reset context
        if (settings && settings.initiator) {
            me.setInitiatorInfo(settings.initiator);
        }
        // call back chat window opening function
        if (settings.chatInvoker && typeof settings.chatInvoker === 'function') {
            settings.chatInvoker();
            me.isChatStarted = true;
        }
    }

    validateName(res) {
        // CHAT-7003
        return validateName(res);
    }

    validatePhone(res) {
        // CHAT-7004
        // I found validatePhone()'s regex passing even an invalkid number like 12345; hence making it accept a custom regex just in case we have to
        // this wone only passes number of the format: 1 555-555-5555
        // sending minimum limit as well along with regex to avoid allowing invalid phoneno lengths.
        return validatePhone(res, [
            {
                regex: /^(1\s?)?((\([0-9]{3}\))|[0-9]{3})[\s\-]?[\0-9]{3}[\s\-]?[0-9]{4}$/g,
                minLimit: 10
            },
            {
                regex: /^((\+61\s?)?(\((0|02|03|04|07|08)\))?)?\s?\d{1,4}\s?\d{1,4}\s?\d{0,4}$/g,
                minLimit: 8
            }
        ]);
    }

    validateEmail(res) {
        // CHAT-7005
        return validateEmailWithEmoji(res);
    }

    validateZipCode(res) {
        return validateZipCode(res);
    }

    handlePreChatMessages(args) {
        const me = this;
        me.phoneValidator.lastMessage = me.lastMessage;
        // apply custom config as per prechat UI
        me.inputContainer = `#apexchat_question_type_text_input_${args.msg.id}`;
        const sendButton = me.context.querySelector(`#apexchat-btn-confirm-${args.msg.id}`);
        const validateInput = function (e) {
            const value = me.context.querySelector(me.inputContainer).value;
            if (me.isLastMessageFullText()) {
                // We are sending message from `validateFullTextMessage` so no need to call `sendResponse`
                me.validateFullTextMessage(value, { isFromPreChat: args.meta.isFromPreChat })
            }
        };
        sendButton?.addEventListener('click', validateInput.bind2(me));
        me.phoneValidator.inputField = me.context.querySelector(me.inputContainer);
        if (args.msg.visitorResponseType === VisitorResponseType.Phone && me.phoneValidator.inputField) {
            me.phoneValidator.inputField.addEventListener('focus', me.addPhoneNumberPrefixCallback);
        } else {
            me.phoneValidator.removePhonePrefixNumber(me.addPhoneNumberPrefixCallback);
        }
        sendButton?.addEventListener('keyup', (e) => {
            if (e.keyCode == 13) {
                validateInput(e);
            }
        });
        me.context.querySelector(me.inputContainer)?.addEventListener('keyup', (e) => {
            if (e.keyCode == 13) {
                validateInput(e);
            }
        });
    }

    /**
     * Check the last phone number response is validated.
     */
    isLastPhoneTypeValidated() {
        const me = this;
        return (
            me.isLastMessageFullText() &&
            !me.isLastFullTextValidated &&
            me.lastMessage &&
            me.lastMessage.visitorResponseType === VisitorResponseType.Phone
        );
    }

    enableTypingAreaForNonRD() {
        const me = this;
        me.$('.livechat_user_input', me.context)
            .removeClass('apexchat_disabled_element')
            .css('pointer-events', 'auto');
    }

    afterRenderMessage(args) {
        const me = this;
        const isSmsOptin = args.msg.systemMessageType === SystemMessageType.SMSOptIn;
        if (isSmsOptin) {
            return;
        }
        me.phoneValidator.lastMessage = this.lastMessage;
        const isNextQuestionMapped =
            args.msg.isNextQuestionMapped ||
            (args.msg.agentScript ? args.msg.agentScript.isNextQuestionMapped : false); // check is next question mapped or not for Non RD case
        if (!me.enableFullTextLogic && !isNextQuestionMapped) {
            // do nothing after rendering a message
            me.enableTypingInput(me.enableTypingAreaForNonRD.bind2(me)); // Classical Standard/Image
            return true;
        }
        if (args.meta.isFromPreChat) {
            // this is custom logic plugged in for PreChat
            me.handlePreChatMessages(args);
        }

        me.currentMsgTypeIsFullText = me.messageAskingForContactInfo(args.msg);
        me.lastMsgTypeIsFullText = me.messageAskingForContactInfo(me.lastMessage);
        // enable visitor to input name/phone/email/message if required or else disable input field
        if (me.isVisitorResponse(args.msg)) {
            const isMapped = isQTMessageMapped(this.lastMessage, args.msg.text || ''); // check if the selection of the last message was mapped
            logIt('QTEXT:: #isVisitorResponse - Disabling typing Input');
            // check last message FT and ismapped then disable typing area
            if (
                (me.lastMsgTypeIsFullText && isMapped) ||
                (me.options.scriptBotEnabled && !me.isTransferredToLiveAgent() && !me.options.model.aiBotEnabled)
            ) {
                // during wait for next question textbox should be disabled on scriptbot
                args.meta.instruction = me.getQTPlaceHolder(); // asking visitor for wait while textbox is already disabled.
                me.disableTypingInput();
            } else if (isMapped) {
                me.disableTypingInput();
            } else {
                args.meta.instruction = me.inputTitle; // set default
                me.enableTypingInput();
            }
            // me.textAreaSelector.unbind('focus');
            // me.phoneValidator.inputField.val('');
            me.phoneValidator.removePhonePrefixNumber(me.addPhoneNumberPrefixCallback);
            me.setAutocompletePlaceHolder(args.meta.instruction);
        } else {
            // is current agent msg FullText? enable with tool tip
            if (me.currentMsgTypeIsFullText) {
                me.isLastFullTextValidated = false; // reset the flag
                args.meta.instruction = args.msg.messagePlaceholder
                    ? args.msg.messagePlaceholder // is a custom placeholder coming with msg? use it
                    : me.getInstructionByResponseType(args.msg.visitorResponseType); // fall back on defaults
                me.makeAutocomplete(args); // when FT messages render then make input autocomplete
                if (args.msg.visitorResponseType === VisitorResponseType.Phone) {
                    if (!me.phoneValidator.inputField) {
                        me.phoneValidator.inputField = me.context.querySelector(me.options.elements.autoCompleteInput);
                    }
                    me.phoneValidator.inputField.addEventListener('focus', me.addPhoneNumberPrefixCallback);
                    // me.textAreaSelector.on('focus', me.addPhoneNumberPrefixCallback);
                } else {
                    me.phoneValidator.removePhonePrefixNumber(me.addPhoneNumberPrefixCallback);
                }
                if (args.msg.isLastSplitMessage) {
                    me.enableTypingInput();
                } else if (args.msg.isSplitMessage) {
                    args.meta.instruction = ScriptsQuestionTypeToolTips.Eng.FTEllipses;
                    me.disableTypingInput();
                } else {
                    me.enableTypingInput();
                }
            } else {
                // normal agent msg? disbale if QT or else enable
                me.phoneValidator.removePhonePrefixNumber(me.addPhoneNumberPrefixCallback);
                if (me.isMessageQT(args.msg)) {
                    logIt('QTEXT:: # Handle enable/disable for an Agent QT Message');
                    // when visitor selected an option switch DOM to windowed to check visitor selected option mapped or not
                    var isMapped = isQTMessageMapped(
                        args.msg,
                        args.msg.visitorSelectedOptions || ''
                    );
                    if (!isMapped || me.options.model.aiBotEnabled) {
                        // if not mapped, enable typing area
                        if (me.options.scriptBotEnabled && !me.isTransferredToLiveAgent() && (!me.options.model.aiBotEnabled || me.options.msg?.isRequiredByAI)) {
                            me.disableTypingInput();
                        } else {
                            args.meta.instruction = args.meta.instruction || me.inputTitle;
                            if (args.msg.isSplitMessage && isMapped) {
                                // when piped messages are mapped then disable typing area
                                me.disableTypingInput();
                            } else {
                                me.enableTypingInput();
                            }
                        }
                    } else {
                        // mapped? disable typing area
                        me.disableTypingInput();
                    }
                    // CHAT-7194-Mobile-Keyboard-Sliding-In-Out
                    // If message is QT we should hide keyboard regardless of map question in mobile device.
                    if (Browser.isAndroid() || Browser.isiOS()) {
                        // me.$(me.inputContainer, me.context).find('textarea').trigger('blur');
                        let inputElem = me.inputContainer;
                        if (typeof me.inputContainer === 'string') {
                            inputElem = me.context.querySelector(me.inputContainer);
                        }
                        const textarea = me.inputContainer.querySelector('textarea');
                        if (textarea) {
                            textarea.blur();
                        }
                    }
                } else {
                    // [Removed "isSplitMessage" Condition] - The code is handled above (in Parent IF)
                    // secondly, it was not correct logically, if the plaintext was splitted or FullText was splitted
                    // system expects the visitor response and it was disabling the textbox.
                    // CHAT-8360 once the end chat msg appeared we need to enable the input field.
                    if (
                        me.options.scriptBotEnabled &&
                        args.msg &&
                        !args.msg.isChatEnded &&
                        !me.isTransferredToLiveAgent()
                        && !me.options.model.aiBotEnabled
                    ) {
                        // CHAT-7200 ScriptBot- plain text from preChat should disable input field
                        args.meta.instruction = ScriptsQuestionTypeToolTips.Eng.FTEllipses;
                        me.disableTypingInput();
                    } else {
                        var isMapped = isQTMessageMapped(args.msg);
                        args.meta.instruction = me.inputTitle;
                        logIt('QTEXT:: # Agent Plain Message - Enabling typing Input...');
                        if ((args.msg.isSplitMessage && isMapped) || args.msg.systemMessageType == SystemMessageType.WebToSMSTransferCompleted) { // when piped messages are mapped then disable typing area
                            if (args.msg.systemMessageType == SystemMessageType.WebToSMSTransferCompleted) {
                                //setting scriptBotEnabled true so input can be disabled
                                me.options.scriptBotEnabled = true;
                                disableTypingInput.bind2(me)(me, me.inputContainer);
                            } else {
                                me.disableTypingInput();
                            }
                        } else {
                            me.enableTypingInput(); // we need input enabled for non-QT, FT and none-QT. Hence commenting out the following lines.
                        }
                        // if (args.msg.isSplitMessage) {
                        //    me.disableTypingInput();
                        // }
                        // else {
                        //    me.enableTypingInput();///needs to be dbl chkd as to how we will get here
                        // }
                        // if (args.msg.questionType == ScriptsQuestionType.FullText) { // split message and FT Predefined enable input field
                        //    me.enableTypingInput();
                        // }
                    }
                }
                me.setAutocompletePlaceHolder(args.meta.instruction);
            }
        }
        if (!isLastMessage(args.msg)) {
            // Show instruction of last message only.
            // CHAT-8442 After load chat, instruction of last message should be visible only on rest message it should show ellipses
            args.meta.instruction = ScriptsQuestionTypeToolTips.Eng.FTEllipses;
        }
        if (isLastMessage(args.msg)) {
            // this condition handle refresh case
            me.displaySkipButton(args.msg);
        }
        me.setInstruction(args.meta.instruction);
        me.setAutoCompleteAttributes(args.msg);
        const autoSendScriptMessage =
            me.options.autoSendScriptMessage || me.options.model?.autoSendScriptMessage;
        const isLastQuestionMapped =
            me.lastMessage &&
            (me.lastMessage.isNextQuestionMapped ||
                (me.lastMessage.agentScript
                    ? me.lastMessage.agentScript.isNextQuestionMapped
                    : false));
        const continueMapping =
            (autoSendScriptMessage ||
                (me.options.scriptBotEnabled && !me.isTransferredToLiveAgent())) &&
            isLastQuestionMapped; //check is next question mapped or not for Non RD case
        me.lastMessage = args.msg;
        if (
            !me.currentMsgTypeIsFullText
            && args.msg.questionType != ScriptsQuestionType.WebToSMSNotification && args.msg.questionType != ScriptsQuestionType.PhoneOTPDialog
            && !(args.msg.questionType == ScriptsQuestionType.None && !args.msg.isSplitMessage && !continueMapping)
        ) {
            // when current QT is not FullText then enable text area
            logIt(`QTEXT:: input toggled msg rendered:${args.msg.text}`);
            me.toggleInputTextArea();
        }
        me.lastMessage.isFullText = args.msg.questionType == ScriptsQuestionType.FullText; // for phone, email, name
        // This is the most appropriate place to do actions after a message is possibly completely rendered
        if (args.msg.beepAfterRender) {
            // playSound functionality now embeded inside a message
            args.msg.beepAfterRender();
        }
        if (args.cb) {
            // optional callback: stopPolling
            const PipedMessageHandlerSingleton = new PipedMessageHandler();
            args.cb.bind2(PipedMessageHandlerSingleton)();
        }
        logIt(`QTEXT:: last msg: ${me.lastMessage.text} rendered; now do else...`);
        me.handleFTInputType(args.msg);
    }

    toggleInputTextArea() {
        const me = this;
        const inputSelector =
            me.initiator === WidgetTypes.ExitPopup
                ? '#apexchat_ep_textarea_input'
                : '#apexchat_rd_msg_box';
        me.context.querySelector('#txtAutocompleteInput')?.style.display = 'none';
        me.context.querySelector('#frmAutocomplete')?.style.display = 'none';
        me.context.querySelector(inputSelector)?.style.display = 'block';
    }

    setAutoCompleteAttributes(msg) {
        // CHAT-6999: set tool tip on input field
        const me = this;
        let inputElem = ''
        if (typeof me.inputContainer === 'string') {
            inputElem = me.context.querySelector(me.inputContainer);
        } else {
            inputElem = me.inputContainer
        }
        if (me.initiator !== WidgetTypes.PreChat) {
            if (typeof me.inputContainer === 'string') {
                inputElem = me.context.querySelector(me.autoCompleteInput);
            } else {
                inputElem = me.autoCompleteInput
            }
        }
        if (!inputElem) return;
        switch (msg.visitorResponseType) {
            case VisitorResponseType.Phone:
                inputElem.setAttribute('name', 'phone');
                inputElem.setAttribute('autocomplete', 'phone');
                break;
            case VisitorResponseType.Email:
                inputElem.setAttribute('name', 'email');
                inputElem.setAttribute('autocomplete', 'email');
                break;
            case VisitorResponseType.Region:
                inputElem.setAttribute('name', 'region');
                inputElem.setAttribute('autocomplete', 'region');
                break;
            case VisitorResponseType.Name:
                inputElem.setAttribute('name', 'name');
                inputElem.setAttribute('autocomplete', 'name');
                break;
            case VisitorResponseType.Address:
                inputElem.setAttribute('name', 'address');
                inputElem.setAttribute('autocomplete', 'address');
                break;
            default:
                // remove setAttributeibutes
                inputElem.setAttribute('autocomplete', 'off');
                inputElem.setAttribute('name', 'text');
                break;
        }
    }

    displaySkipButton(msg) {
        const me = this;
        let skipButtonText = ''

        if (msg && msg.isSkippable) {
            switch (msg.visitorResponseType) {
                case VisitorResponseType.Name:
                    skipButtonText = 'Skip Name';
                    break;
                case VisitorResponseType.Phone:
                    skipButtonText = 'Skip Phone Number';
                    break;
                case VisitorResponseType.Email:
                    skipButtonText = 'Skip Email Address';
                    break;
                case VisitorResponseType.Region:
                    skipButtonText = 'Skip Region';
                    break;
                case VisitorResponseType.Message:
                    skipButtonText = 'Skip Message';
                    break;
                case VisitorResponseType.Address:
                    skipButtonText = 'Skip Address';
                    break;
            }
            const skipButton = me.context.querySelector('.apexchat_skip_input');
            if (skipButton) {
                // check skip button exists or not
                me.skipFTButtonADA(skipButton, skipButtonText); // skip button function for ADA
            }
        } else {
            me.context.querySelector('.apexchat_skip_input')?.style.display = 'none'
        }
    }

    skipFTButtonADA(element, skipButtonText) {
        const me = this;
        const $element = me.$(element, me.context);
        $element.unbind(); // unbind all existing event
        $element[0].innerText = skipButtonText;
        $element[0].title = skipButtonText;
        ManageFocusOnKeyEvents(element); // on focus border show
        $element.show();

        me.$($element).on('keyup click', (e) => {
            // check if button already not selected then do the action
            e.stopPropagation();
            if (e.type == 'click' || (e.type == 'keyup' && isAcceptKey(e))) {
                me.skipFullText.bind2(me)();
            }
        });
    }
    // skipFTButtonADA(element, skipButtonText) {
    //     const me = this;

    //     // Remove all existing event listeners
    //     const newElement = element.cloneNode(true);
    //     element.replaceWith(newElement);
    //     element = newElement; // Update reference to the new element

    //     // Set button text and title
    //     element.innerText = skipButtonText;
    //     element.title = skipButtonText;

    //     // Manage focus styling
    //     ManageFocusOnKeyEvents(element);

    //     // Make element visible
    //     element.style.display = 'block';

    //     // Add event listeners
    //     element.addEventListener('click', (e) => {
    //         e.stopPropagation();
    //         me.skipFullText.bind2(me)();
    //     });

    //     element.addEventListener('keyup', (e) => {
    //         e.stopPropagation();
    //         if (isAcceptKey(e)) {
    //             me.skipFullText.bind2(me)();
    //         }
    //     });
    // }

    isMessageQT(msg) {
        const me = this;
        // IF QuestionType is greater than 0 but no 8 (which is FullText)
        return (
            msg && msg.questionType > 0 && msg.questionType != ScriptsQuestionType.FullText
        );
    }

    isVisitorResponse(msg) {
        const me = this;
        const lastMsg = msg || me.lastMessage;
        return lastMsg.questionType == 0 && !lastMsg.userId;
    }

    isLastPlainMessage() {
        const me = this;
        return (
            me.lastMessage &&
            me.lastMessage.questionType == 0 &&
            me.lastMessage.visitorResponseType == VisitorResponseType.Message
        );
    }

    messageAskingForContactInfo(msg) {
        // CHAT-6979-detect if it is FullText type of Name/Phone/Email
        const me = this;
        return (
            msg &&
            msg.questionType === ScriptsQuestionType.FullText &&
            ((msg.visitorResponseType &&
                msg.visitorResponseType == VisitorResponseType.Name) ||
                msg.visitorResponseType == VisitorResponseType.Phone ||
                msg.visitorResponseType == VisitorResponseType.Email ||
                msg.visitorResponseType == VisitorResponseType.Region ||
                msg.visitorResponseType == VisitorResponseType.Message ||
                msg.visitorResponseType == VisitorResponseType.Address)
        );
    }

    getInstructionByResponseType(type) {
        const me = this;
        switch (type) {
            case VisitorResponseType.Name:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTName
                    : ScriptsQuestionTypeToolTips.Eng.FTName;
            case VisitorResponseType.Email:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTEmail
                    : ScriptsQuestionTypeToolTips.Eng.FTEmail;
            case VisitorResponseType.Phone:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTPhone
                    : ScriptsQuestionTypeToolTips.Eng.FTPhone;
            case VisitorResponseType.Message:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTMessage
                    : ScriptsQuestionTypeToolTips.Eng.FTMessage;
            case VisitorResponseType.Region:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTZipcode
                    : ScriptsQuestionTypeToolTips.Eng.FTZipcode;
            case VisitorResponseType.Address:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTAddress
                    : ScriptsQuestionTypeToolTips.Eng.FTAddress;
            default:
                return checkSpanishLanguage(me.options)
                    ? ScriptsQuestionTypeToolTips.Spa.FTMessage
                    : ScriptsQuestionTypeToolTips.Eng.FTMessage;
        }
    }

    isLastMessageFullText() {
        const me = this;
        return me.lastMessage && me.lastMessage.isFullText;
    }

    resizePreChat() {
        const me = this;
        // resizing prechat as per the height of the content of the talk bubble
        if (typeof me.questionTypeRenderer?.options?.prechatResizeHandler === 'function') {
            me.questionTypeRenderer?.options?.prechatResizeHandler.bind2(me)({ animate: false });
        } else {
            me.postMessage({
                event: 'prechat-auto-resizing',
                data: {
                    isErrorMsgForFT: true,
                    errorMsgHeight: 160
                }
            });
        }
    }

    showError(error) {
        // CHAT-7038:
        const me = this;
        const inputContainer = typeof me.inputContainer === 'string' ? me.context.querySelector(me.inputContainer) : me.inputContainer;
        logIt('QTEXT:: input field is showing error on on invalid input');
        const toolTipCss = me.isFromPreChat
            ? 'apexchat_prechat_fulltext_error'
            : 'livechat_validation_tooltip';
        showValidationTooltip(inputContainer, {
            toolTipText: error,
            toolTipCss,
            isPreChatText: me.isFromPreChat
        });
        if (inputContainer.classList.contains('livechat_validation_tooltip')) {
            // check class exists
            me.context.querySelector(me.options.elements.textboxContainerSelector,).classList.add(
                'livechat_tooltip_Error_Enabled'
            );
        }
        me.resizePreChat();
    }

    disableTypingInput(instruction) {
        // CHAT-7038: disable input
        const shouldDisableInputContainer = function (msg) {
            if (msg) {
                return msg.questionType != ScriptsQuestionType.Text;
            }
        };
        const me = this;
        if (!me.enableFullTextLogic) {
            return;
        }
        logIt('QTEXT:: input field is disabled now.');
        if (
            me.options &&
            (me.options.autoSendScriptMessage == ChatFlowEnableFlag.Auto_Send ||
                me.options.scriptBotEnabled)
        ) {
            // if scriptBotEnabled true, we need input field to be locked no matter auto send true or false
            if ((!me.options.model.aiBotEnabled || me.options.msg?.isRequiredByAI)) {
                if (shouldDisableInputContainer(me.options.msg)) {
                    disableTypingInput.bind2(me)(me, me.inputContainer, instruction);
                } else {
                    me.enableTypingInput();
                    disableTypingInput.bind2(me)(me, me.textAreaSelector, instruction);
                }
            }

        }
    }

    enableTypingInput(cb) {
        // CHAT-7038: enable input
        const me = this;
        logIt('QTEXT:: input field is enabled now.');
        if (typeof me.inputContainer === 'string') {
            enableTypingInput.bind2(me)(me, me.context.querySelector(me.inputContainer));
        } else {
            enableTypingInput.bind2(me)(me, me.inputContainer);
        }

        if (cb && typeof cb === 'function') {
            // callback function
            cb();
        }
    }

    setInstruction(placeholderText) {
        // CHAT-6999: set placeholderText on input field
        const me = this;
        if (!me.isChatStarted) {
            return;
        }
        const textAreaIndex = me.initiator === WidgetTypes.ExitPopup ? 1 : 0; // index of textarea in EP is different from DOM so we need to mention index where textarea lie
        let inputElem = ''
        if (typeof me.inputContainer === 'string') {
            inputElem = me.context.querySelector(me.inputContainer);
        } else {
            inputElem = me.inputContainer
        }

        if (inputElem) {
            const textarea = inputElem.querySelector('textarea');
            if (textarea) {
                setPlaceholder(textarea, placeholderText, textAreaIndex);
            }
        }

        setPlaceholder(inputElem, placeholderText, textAreaIndex);
        logIt(`QTEXT:: has a placeholderText: ${placeholderText}`);
    }

    // this either sets a new text value or an old one
    setLastFullText(text) {
        const me = this;
        text = text || TempStorageCache.get(TempCache.INPUT_OLD_VALUE); // if no text specified, set oldVal
        try {
            // const $txt = me.$(me.inputContainer, me.context).first().find('textarea');
            if (typeof me.inputContainer === 'string') {
                inputElem = me.context.querySelector(me.inputContainer);
            } else {
                inputElem = me.inputContainer
            }
            const txt = me.inputContainer.querySelector('textarea');

            if (txt) {
                txt.value = text;
            }
        } catch (e) {
            // must be breaking due to some null
            logIt(`setLastFullText:${e.message}`);
        }
    }

    /**
     * return visitor response type
     */
    toAutocompleteType(visitorResponseType) {
        switch (visitorResponseType) {
            case VisitorResponseType.Name:
                return 'name';
            case VisitorResponseType.Email:
                return 'email';
            case VisitorResponseType.Phone:
                return 'phone';
            case VisitorResponseType.Region:
                return 'region';
        }
    }

    /**
     *
     * @param {args} args initialize auto complete options
     */
    makeAutocomplete(args) {
        const me = this;
        if (me.options.widgetType === WidgetTypes.PreChat) {
            // do not show autocomplete if inputSelectorhat
            // if we put message type in this condition it will create other issues.
            return;
        }
        const autoComplete = new AutoComplete({
            enableAutoComplete:
                args.msg.visitorResponseType === VisitorResponseType.Message ||
                    isMobile()
                    ? 'off'
                    : 'on',
            type: me.toAutocompleteType(args.msg.visitorResponseType),
            // id: 'apexchat_rd_msg_box',
            className: 'apexchat_ada_compliance',
            placeholder: args.meta.instruction,
            title: 'Type your response and press Return or Send',
            // containerSelector: me.options.elements.inputContainerSelector,
            // autocompleteOnOff: 'off',
            inputSelector: '#txtAutocompleteInput',
            textareaSelector:
                me.initiator === WidgetTypes.ExitPopup
                    ? '#apexchat_ep_textarea_input'
                    : '#apexchat_rd_msg_box',
            context: me.context
        });
    }

    setAutocompletePlaceHolder(tip) {
        const me = this;
        const input = me.context.querySelector('#txtAutocompleteInput');
        input?.setAttribute('placeholder', tip);
        input?.setAttribute('name', '');
        input?.setAttribute('autocomplete', 'off');
    }
    handleFTInputType(m) {
        const me = this;
        let inputType = 'text'
        if (m.questionType == ScriptsQuestionType.FullText) {
            switch (m.visitorResponseType) {
                case VisitorResponseType.Phone:
                    inputType = 'tel';
                    break;
                case VisitorResponseType.Email:
                    inputType = 'email';
                    break;
            }
        }

        me.context.querySelector('#txtAutocompleteInput')?.setAttribute('type', inputType);
    }
}
